<script setup lang="ts">
import type { Personnel } from '@/types'
import { MedicalQual, RopeQual } from '@/types'
import FosTooltip from '@/components/ui/FosTooltip.vue'
import { useDragState } from '@/composables/useDragState'

const props = withDefaults(
  defineProps<{
    person: Personnel
    side?: 'top' | 'bottom' | 'left' | 'right'
  }>(),
  { side: 'right' },
)

const { isDragging } = useDragState()

const q = props.person.qualifications ?? {}
const hasContent =
  (q.searcher_type != null && q.searcher_type !== 'None') ||
  (q.medical_qual != null && q.medical_qual !== MedicalQual.None) ||
  (q.rope_qual != null && q.rope_qual !== RopeQual.None) ||
  ((q.misc_quals as string[] | undefined)?.length ?? 0) > 0 ||
  !!props.person.phone?.trim() ||
  !!props.person.notes?.trim()
</script>

<template>
  <FosTooltip v-if="(hasContent || person.time_out !== null) && !isDragging" :side="side">
    <slot />
    <template #content>
      <div class="flex flex-col gap-1.5">
        <div>
          <p class="font-semibold tracking-wide text-surface">{{ person.last }}, {{ person.first }}</p>
          <p class="text-surface/60 uppercase tracking-widest">{{ person.agency }}</p>
        </div>
        <div v-if="person.time_out !== null" class="flex items-center gap-1.5">
          <span class="w-1.5 h-1.5 rounded-full bg-outline-variant shrink-0" />
          <span class="text-surface/60 text-[0.65rem] font-mono uppercase tracking-widest">Checked Out</span>
        </div>
        <div class="h-px bg-surface/20" />
        <div class="flex flex-col gap-1">
          <div v-if="q.searcher_type && q.searcher_type !== 'None'" class="flex gap-2">
            <span class="text-surface/50 w-14 shrink-0">Searcher</span>
            <span class="text-surface">{{ q.searcher_type }}</span>
          </div>
          <div v-if="q.medical_qual && q.medical_qual !== MedicalQual.None" class="flex gap-2">
            <span class="text-surface/50 w-14 shrink-0">Medical</span>
            <span class="text-surface">{{ q.medical_qual }}</span>
          </div>
          <div v-if="q.rope_qual && q.rope_qual !== RopeQual.None" class="flex gap-2">
            <span class="text-surface/50 w-14 shrink-0">Rope</span>
            <span class="text-surface">{{ q.rope_qual }}</span>
          </div>
          <div v-if="(q.misc_quals as string[] | undefined)?.length" class="flex gap-2">
            <span class="text-surface/50 w-14 shrink-0">Other</span>
            <span class="text-surface">{{ (q.misc_quals as string[]).join(', ') }}</span>
          </div>
          <div v-if="person.phone?.trim()" class="flex gap-2">
            <span class="text-surface/50 w-14 shrink-0">Phone</span>
            <span class="text-surface">{{ person.phone }}</span>
          </div>
        </div>
        <template v-if="person.notes?.trim()">
          <div class="h-px bg-surface/20" />
          <p class="text-surface/70 italic">{{ person.notes }}</p>
        </template>
      </div>
    </template>
  </FosTooltip>
  <slot v-else />
</template>
